import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlag } from 'featureFlags';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import {
  captureBaseline,
  clearBaseline,
  dumpBaseline,
  getBaseline,
  updateHudSnapshot
} from 'utils/baseline';
import type { BenchmarkReport } from 'utils/benchmark';
import {
  benchmarkRemainingMs,
  cancelObserve,
  clearBenchmarks,
  dumpBenchmarks,
  finishBenchmark,
  getBenchmarks,
  isBenchmarkManualEnd,
  isBenchmarkRunning,
  isTableControllerReady,
  observeTables,
  openGridViaController,
  openObserveTable,
  pickLiveTables,
  returnToLobbyViaController,
  sampleBenchmark,
  shareBenchmark,
  startBenchmark
} from 'utils/benchmark';
import {
  clearCapture,
  clearReplayedTables,
  exportRecording,
  getCaptureTables,
  getFrameCount,
  getReplayProgress,
  isRecording,
  isReplaying,
  isReplayPaused,
  loadRecording,
  pauseReplay,
  primeFirstReplayTable,
  recordingStats,
  replayRecording,
  resumeReplay,
  setReplaying,
  setReplaySpeed,
  startRecording,
  stopRecording,
  stopReplay
} from 'utils/handRecorder';
import { drainPerfCounters, readLatency, setLatencyProbe } from 'utils/perfCounters';
import {
  getSpikeSummary,
  logSpikeSummary,
  resetSpikes,
  shareSpikeSummary,
  startSpikeRecorder,
  stopSpikeRecorder
} from 'utils/spikeRecorder';
import {
  getSwitchProfileSummary,
  logSwitchProfiles,
  resetSwitchProfiles,
  setSwitchProfilingEnabled
} from 'utils/switchProfile';

import BenchmarkReportModal from 'components/Debug/BenchmarkReportModal';
import HandPaceRows from 'components/Debug/HandPaceRows';
import {
  isPlayerPerfHudEnabled,
  PERF_HUD_TOGGLE_EVENT,
  setPlayerPerfHudEnabled
} from 'components/Debug/perfHudPreference';

// Lightweight on-screen perf HUD. OFF BY DEFAULT and available only to players
// targeted by the feature flag. Those players can enable it from Settings >
// Advanced; the choice is stored per player and survives reloads.
// Updates the visible panel 4 times per second (cheap), but the underlying
// frame-time / long-task counters update every frame so we don't miss spikes.

// Remembered panel position (persisted), so the HUD stays where you drag it
// across reloads. null = the default top-right anchor.
const POS_STORAGE_KEY = 'hijack_perfhud_pos';
type HudPos = { left: number; top: number };
const loadPos = (): HudPos | null => {
  try {
    const raw = window.localStorage.getItem(POS_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.left === 'number' && typeof p?.top === 'number')
      return { left: p.left, top: p.top };
    return null;
  } catch {
    return null;
  }
};
const savePos = (p: HudPos): void => {
  try {
    window.localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(p));
  } catch {
    // localStorage unavailable — position still applies for this session.
  }
};
// A drag must move at least this far before it counts as a drag (vs. a tap).
const DRAG_THRESHOLD_PX = 4;
const HUD_BUTTON_STYLE = {
  appearance: 'none',
  background: 'none',
  border: 0,
  font: 'inherit',
  lineHeight: 'inherit',
  padding: 0
} as const;

// `performance.memory` is non-standard: present on Chrome / Android WebView,
// ABSENT on iOS Safari / WKWebView. Read defensively.
type PerfMemory = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};
const readMemory = (): PerfMemory | null => {
  try {
    const m = (performance as unknown as { memory?: PerfMemory }).memory;
    if (!m || typeof m.usedJSHeapSize !== 'number') return null;
    return m;
  } catch {
    return null;
  }
};
const MB = 1024 * 1024;

// Leak detection works off the post-GC "floor" rather than the instantaneous
// heap. The live heap sawtooths with every GC, which makes a raw growth rate
// flicker uselessly. We bucket samples into FLOOR_BUCKET_MS windows, keep each
// bucket's MINIMUM (≈ the heap right after a collection), and fit a line
// through those minima. A floor that ratchets upward is a real leak.
const FLOOR_BUCKET_MS = 10000;
const MAX_FLOOR_BUCKETS = 18; // ~3 minutes of history
const MIN_BUCKETS_FOR_TREND = 3; // ~30s before we trust the slope

// Least-squares slope of {t (ms), mb} points, returned as MB per minute.
const slopeMbPerMin = (pts: Array<{ t: number; mb: number }>): number => {
  const n = pts.length;
  if (n < 2) return 0;
  const t0 = pts[0].t;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let sxy = 0;
  for (const p of pts) {
    const x = (p.t - t0) / 60000; // minutes since first point
    const y = p.mb;
    sx += x;
    sy += y;
    sxx += x * x;
    sxy += x * y;
  }
  const denom = n * sxx - sx * sx;
  if (denom === 0) return 0;
  return (n * sxy - sx * sy) / denom;
};

// The sample interval; rates are scaled from per-sample totals to per-second.
const SAMPLE_MS = 250;

// Ignore the first stretch after the HUD starts when recording session
// peaks/lows: mount + first navigation spike frame times and FPS badly, which
// would otherwise dominate the peaks for the rest of the session and make them
// useless. The live values still update during warm-up; only the extremes wait.
const PEAK_WARMUP_MS = 2500;

// Count CSS + Web Animations API animations currently running document-wide.
// This is the HUD's blind spot otherwise — `activeTweens` only sees GSAP, not
// the infinite CSS keyframe animations on lobby tiles, ticker marquee, etc.
const countRunningAnimations = (): number => {
  try {
    if (typeof document === 'undefined' || typeof document.getAnimations !== 'function') {
      return -1; // unsupported
    }
    let running = 0;
    for (const a of document.getAnimations()) {
      if (a.playState === 'running') running++;
    }
    return running;
  } catch {
    return -1;
  }
};

type Stats = {
  fps: number;
  // Lowest FPS seen across the session (0 = no valid reading yet). Survives the
  // per-sample reset, so a momentary frame-rate dip isn't lost.
  fpsMin: number;
  worstFrameMs: number;
  // Worst frame seen across the whole session (survives the per-sample reset).
  // A single bad spike that the rolling readout would otherwise wipe in 250ms.
  worstFrameMaxMs: number;
  activeTweens: number;
  // Highest active-tween count seen this session — a climbing peak is the tell
  // for a tween leak (the thing tap-to-dump exists to hunt down).
  activeTweensMax: number;
  // Running CSS + Web animations document-wide. -1 = unsupported.
  runningAnims: number;
  // Highest running-animation count seen this session (-1 = unsupported).
  runningAnimsMax: number;
  // Inbound socket-payload decompressions per second, and the CPU ms/sec they
  // cost. The idle-heat smoking gun: high decompress ms/sec while doing nothing.
  msgsPerSec: number;
  decompressMsPerSec: number;
  // API Gateway WebSocket true round-trip time (ms). -1 = no sample yet.
  wsRttMs: number;
  // Worst (highest) RTT seen across the session. -1 = no sample yet.
  wsRttMaxMs: number;
  // RTT reading is old / unanswered (probe off or no pong) — show it greyed.
  wsRttStale: boolean;
  // Socket.IO action-socket stream age: ms since the last inbound message.
  // -1 = nothing received yet. Liveness, not latency (see perfCounters).
  actionAgeMs: number;
  // Memory (MB). -1 = unsupported on this platform (e.g. iOS WKWebView).
  heapUsedMb: number;
  heapPeakMb: number;
  heapFloorMb: number; // lowest post-GC heap seen recently
  heapLeakMbPerMin: number; // slope of the floor — the real leak signal
  heapLeakReady: boolean; // false until we have enough buckets to trust it
  // Long-frame spikes (LoAF/longtask). Session total, worst blocking ms, and how
  // many landed in the last sample window (the "it's happening right now" tell).
  spikeCount: number;
  spikeWorstMs: number;
  spikesInWindow: number;
  // false on iOS WKWebView / Safari (no LoAF or longtask) — spike row shows n/a.
  spikeSupported: boolean;
  // Table-switch attribution (utils/switchProfile): switches measured, their
  // worst blocking frame, and the mean main-thread ms each switch cost. This is
  // the row that answers "how much of the jank is switching tables?".
  switchCount: number;
  switchWorstMs: number;
  switchAvgMs: number;
};

const PerfHud = () => {
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const performanceHudAvailable = useFeatureFlag('performanceHud');
  const readEnabled = useCallback(
    () => performanceHudAvailable && !!playerGUID && isPlayerPerfHudEnabled(playerGUID),
    [performanceHudAvailable, playerGUID]
  );
  const [enabled, setEnabled] = useState(readEnabled);
  const visible = enabled && readEnabled();

  useEffect(() => {
    const syncPreference = () => setEnabled(readEnabled());
    syncPreference();
    window.addEventListener(PERF_HUD_TOGGLE_EVENT, syncPreference);
    return () => window.removeEventListener(PERF_HUD_TOGGLE_EVENT, syncPreference);
  }, [readEnabled]);

  const [stats, setStats] = useState<Stats>({
    fps: 0,
    fpsMin: 0,
    worstFrameMs: 0,
    worstFrameMaxMs: 0,
    activeTweens: 0,
    activeTweensMax: 0,
    runningAnims: -1,
    runningAnimsMax: -1,
    msgsPerSec: 0,
    decompressMsPerSec: 0,
    wsRttMs: -1,
    wsRttMaxMs: -1,
    wsRttStale: true,
    actionAgeMs: -1,
    heapUsedMb: -1,
    heapPeakMb: -1,
    heapFloorMb: -1,
    heapLeakMbPerMin: 0,
    heapLeakReady: false,
    spikeCount: 0,
    spikeWorstMs: 0,
    spikesInWindow: 0,
    spikeSupported: false,
    switchCount: 0,
    switchWorstMs: 0,
    switchAvgMs: 0
  });

  // Benchmark UI: countdown seconds while a run is active (null = idle), and
  // whether at least one report exists this session (enables the share button).
  const [benchRemainingSec, setBenchRemainingSec] = useState<number | null>(null);
  const [hasReport, setHasReport] = useState<boolean>(() => getBenchmarks().length > 0);
  // The finished report to show in the centered modal (null = hidden).
  const [reportModal, setReportModal] = useState<BenchmarkReport | null>(null);
  // Progress label shown next to the countdown while tables are being opened
  // (the opening itself is measured — it's part of normal use).
  const [benchPhase, setBenchPhase] = useState<string>('');
  // Number of tables the observe benchmark opens (the max-load use case).
  const OBSERVE_TABLE_COUNT = 4;

  // Hand-recorder UI state: whether we're recording, the live frame count, and
  // whether a finished capture is available to export/replay.
  const [recActive, setRecActive] = useState<boolean>(false);
  const [recFrames, setRecFrames] = useState<number>(0);
  const [recHasCapture, setRecHasCapture] = useState<boolean>(false);
  // Whether a replay is currently running, and whether it's paused — reflected
  // from the recorder each sample tick so the stop/pause buttons show correctly.
  const [replayActive, setReplayActive] = useState<boolean>(false);
  const [replayPausedUi, setReplayPausedUi] = useState<boolean>(false);
  // Live replay progress (frames applied / total) and the selected speed.
  const [replayIndexUi, setReplayIndexUi] = useState<number>(0);
  const [replayTotalUi, setReplayTotalUi] = useState<number>(0);
  const [replaySpeedUi, setReplaySpeedUi] = useState<number>(1);
  // Hidden file input for importing an exported recording .json.
  const importInputRef = useRef<HTMLInputElement | null>(null);
  // Router navigate — used to open a live table in observe mode. PerfHud renders
  // under BrowserRouter (sibling of <Routes>), so this is available.
  const navigate = useNavigate();
  // The benchmark opens a table FROM the lobby, so the control only makes sense
  // there ('/' is the lobby view; '/table/*' and '/grid' are not).
  const isLobby = useLocation().pathname === '/';
  // Latest observe-bench handler, so the console hook always calls the current
  // closure, and the warm-up timeout id so we can cancel it if the HUD closes.
  const observeHandlerRef = useRef<() => void>(() => {});

  // Draggable panel: remembered position (null = default top-right), a ref to the
  // panel element (to measure it), and a flag set during a drag so the drag's
  // trailing click/touch doesn't also fire the tween-dump.
  const [pos, setPos] = useState<HudPos | null>(loadPos);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const didDragRef = useRef<boolean>(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  const frameTimesRef = useRef<number[]>([]);
  const lastFrameTsRef = useRef<number>(0);
  const rafIdRef = useRef<number>(0);

  // Session peaks/lows: the worst values seen since the HUD started, so a
  // momentary spike or dip isn't lost when the rolling readout resets. These
  // persist for the life of the tab (closing + reopening the HUD keeps them);
  // reload, or tap the ↺ button, to clear.
  const fpsMinRef = useRef<number>(0); // 0 = no valid reading yet
  const worstFramePeakRef = useRef<number>(0);
  const wsRttPeakRef = useRef<number>(-1);
  const tweensPeakRef = useRef<number>(0);
  const animsPeakRef = useRef<number>(-1);
  // Wall-clock after which peak/low tracking is allowed (set when sampling
  // starts; the ↺ reset button does NOT re-arm it, since a manual reset means
  // "track from now"). 0 = not yet armed.
  const peakWarmupUntilRef = useRef<number>(0);

  // Memory tracking. peak survives the whole session. Leak detection buckets
  // samples and keeps each bucket's post-GC minimum (the "floor"); a line fit
  // through the floors gives a stable leak rate instead of GC-sawtooth noise.
  const heapPeakMbRef = useRef<number>(-1);
  const floorBucketMinRef = useRef<number>(Infinity);
  const floorBucketStartRef = useRef<number>(0);
  const floorHistoryRef = useRef<Array<{ t: number; mb: number }>>([]);

  // Spike total at the previous sample, so we can show how many long frames
  // landed in just the last window (a live "spiking now" signal vs. the total).
  const prevSpikeTotalRef = useRef<number>(0);

  useEffect(() => {
    if (!visible) return;

    // Arm the peak/low warm-up so startup spikes don't pollute the extremes.
    if (peakWarmupUntilRef.current === 0) {
      peakWarmupUntilRef.current = performance.now() + PEAK_WARMUP_MS;
    }

    // Ask the WebSocket adapter to start probing API Gateway RTT frequently
    // while the HUD is up. Off again on cleanup so production never pays for it.
    setLatencyProbe(true);

    // Attach the long-frame spike recorder (LoAF/longtask). Same deal: only runs
    // while the HUD is up, and even then only fires on frames that blew the
    // budget, so it's effectively free. Off again on cleanup.
    startSpikeRecorder();

    // Bracket each table switch so its long frames can be attributed to it —
    // one timer per switch, and only while the HUD is up.
    setSwitchProfilingEnabled(true);

    // Expose console hooks while the HUD is open (out of production otherwise,
    // same as the gsap handle):
    //   dumpSpikes()               — spike/offender table for the current run
    //   captureBaseline('label')   — save a labeled snapshot of this run
    //   dumpBaseline()             — table of every saved capture
    //   getBaseline()              — full JSON (use copy(getBaseline()))
    //   clearBaseline()            — wipe saved captures
    (window as any).dumpSpikes = logSpikeSummary;
    (window as any).captureBaseline = captureBaseline;
    (window as any).dumpBaseline = dumpBaseline;
    (window as any).getBaseline = getBaseline;
    (window as any).clearBaseline = clearBaseline;
    // Benchmark console hooks (mirror the ▶/⇪ HUD buttons):
    //   runBenchmark('label', 60)  — start a timed run (HUD must stay open)
    //   dumpBenchmarks()           — comparison table of every saved run
    //   getBenchmarks()            — full JSON (copy(getBenchmarks()))
    //   shareBenchmark()           — share/copy the most recent report
    //   clearBenchmarks()          — wipe saved reports
    (window as any).runBenchmark = (label = 'run', sec = 60) => {
      startBenchmark(label, sec);
      setBenchRemainingSec(sec);
    };
    // Auto-observe the busiest live table then benchmark it (mirrors ▶ observe).
    (window as any).observeBenchmark = () => observeHandlerRef.current();
    // Hand-replay recorder PROTOTYPE (console-driven while sizing the data):
    //   handRec.start()   — begin capturing table frames (sit on a table)
    //   handRec.stats()   — size report for the current capture
    //   handRec.stop()    — stop + print the size report
    //   handRec.export()  — download the recording as JSON to share
    //   handRec.replay(s) — replay it (speed s, default 1) through the real path
    //   handRec.clear()   — free the in-memory capture (reclaim its ~10MB+)
    (window as any).handRec = {
      start: startRecording,
      stop: stopRecording,
      stats: recordingStats,
      export: exportRecording,
      replay: replayRecording,
      clear: clearCapture
    };
    //   dumpSwitches()             — per-table-switch cost table (spikes + offender)
    (window as any).dumpSwitches = logSwitchProfiles;
    (window as any).dumpBenchmarks = dumpBenchmarks;
    (window as any).getBenchmarks = getBenchmarks;
    (window as any).shareBenchmark = shareBenchmark;
    (window as any).clearBenchmarks = () => {
      clearBenchmarks();
      setHasReport(false);
    };

    // Expose gsap on window so the console can poke at it for ad-hoc
    // diagnostics. Only when the HUD is enabled, so this stays out of
    // production builds for everyone else.
    (window as any).gsap = gsap;

    // Per-frame: capture the gap between RAF callbacks. Sliding 1-second window.
    const tick = (ts: number) => {
      if (lastFrameTsRef.current > 0) {
        const dt = ts - lastFrameTsRef.current;
        frameTimesRef.current.push(dt);
        // Keep last ~1 second worth (assume up to 120 frames at 120Hz).
        if (frameTimesRef.current.length > 120) frameTimesRef.current.shift();
      }
      lastFrameTsRef.current = ts;
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);

    // Sample stats 4×/sec, then reset rolling counters so the readout
    // reflects the last quarter-second rather than session totals.
    const sampleId = window.setInterval(() => {
      const frames = frameTimesRef.current;
      const totalMs = frames.reduce((s, n) => s + n, 0);
      const fps = totalMs > 0 ? Math.round((frames.length * 1000) / totalMs) : 0;
      const worst = frames.length > 0 ? Math.max(...frames) : 0;

      // Only fold values into the session peaks/lows once warm-up has elapsed,
      // so startup/navigation spikes don't permanently dominate the extremes.
      const trackPeaks = performance.now() >= peakWarmupUntilRef.current;
      // Track the session-low FPS. Ignore 0 (no frames sampled yet) so the min
      // reflects a real low, not the startup gap before the first frames land.
      if (trackPeaks && fps > 0 && (fpsMinRef.current === 0 || fps < fpsMinRef.current)) {
        fpsMinRef.current = fps;
      }
      if (trackPeaks && worst > worstFramePeakRef.current) worstFramePeakRef.current = worst;

      const activeTweens = gsap.globalTimeline.getChildren(true, true, false).length;
      if (trackPeaks && activeTweens > tweensPeakRef.current) tweensPeakRef.current = activeTweens;

      const runningAnims = countRunningAnimations();
      if (trackPeaks && runningAnims > animsPeakRef.current) animsPeakRef.current = runningAnims;

      // Memory: read JS heap (when supported), track peak + post-GC floor.
      const mem = readMemory();
      let heapUsedMb = -1;
      let heapPeakMb = heapPeakMbRef.current;
      let heapFloorMb = -1;
      let heapLeakMbPerMin = 0;
      let heapLeakReady = false;
      if (mem) {
        heapUsedMb = mem.usedJSHeapSize / MB;
        if (trackPeaks && heapUsedMb > heapPeakMbRef.current) heapPeakMbRef.current = heapUsedMb;
        heapPeakMb = heapPeakMbRef.current;

        const now = performance.now();
        if (floorBucketStartRef.current === 0) floorBucketStartRef.current = now;
        // Track the minimum within the current bucket (≈ post-GC floor).
        floorBucketMinRef.current = Math.min(floorBucketMinRef.current, heapUsedMb);

        // Close the bucket once it's full and push its floor to history.
        if (now - floorBucketStartRef.current >= FLOOR_BUCKET_MS) {
          const hist = floorHistoryRef.current;
          hist.push({ t: now, mb: floorBucketMinRef.current });
          while (hist.length > MAX_FLOOR_BUCKETS) hist.shift();
          floorBucketMinRef.current = Infinity;
          floorBucketStartRef.current = now;
        }

        const hist = floorHistoryRef.current;
        // Show the most recent settled floor, falling back to the live value.
        heapFloorMb = hist.length > 0 ? hist[hist.length - 1].mb : heapUsedMb;
        if (hist.length >= MIN_BUCKETS_FOR_TREND) {
          heapLeakMbPerMin = slopeMbPerMin(hist);
          heapLeakReady = true;
        }
      }

      // Drain the global counters and convert per-sample totals to per-second.
      const drained = drainPerfCounters();
      const rateFactor = 1000 / SAMPLE_MS;

      // Latency: API Gateway true RTT, plus Socket.IO stream age. A RTT older
      // than a few probe cycles means the probe isn't getting pongs — flag it.
      const lat = readLatency();
      const nowMs = Date.now();
      const actionAgeMs = lat.actionMsgAt > 0 ? nowMs - lat.actionMsgAt : -1;
      const wsRttStale = lat.wsRttAt === 0 || nowMs - lat.wsRttAt > 15000;
      // Track the worst RTT seen. Re-maxing the same persisted value between
      // samples is harmless; the peak only climbs when a higher RTT is recorded.
      if (trackPeaks && lat.wsRttMs >= 0 && lat.wsRttMs > wsRttPeakRef.current)
        wsRttPeakRef.current = lat.wsRttMs;

      // Long-frame spikes: session total + worst, plus how many landed since the
      // previous sample (the live "spiking now" delta).
      const spikes = getSpikeSummary();
      const spikesInWindow = Math.max(0, spikes.total - prevSpikeTotalRef.current);
      prevSpikeTotalRef.current = spikes.total;

      // Per-table-switch cost, attributed from those same spikes.
      const switches = getSwitchProfileSummary();

      const next: Stats = {
        fps,
        fpsMin: fpsMinRef.current,
        worstFrameMs: worst,
        worstFrameMaxMs: worstFramePeakRef.current,
        activeTweens,
        activeTweensMax: tweensPeakRef.current,
        runningAnims,
        runningAnimsMax: animsPeakRef.current,
        msgsPerSec: Math.round(drained.messages * rateFactor),
        decompressMsPerSec: Math.round(drained.decompressMs * rateFactor),
        wsRttMs: lat.wsRttMs,
        wsRttMaxMs: wsRttPeakRef.current,
        wsRttStale,
        actionAgeMs,
        heapUsedMb,
        heapPeakMb,
        heapFloorMb,
        heapLeakMbPerMin,
        heapLeakReady,
        spikeCount: spikes.total,
        spikeWorstMs: spikes.worstMs,
        spikesInWindow,
        spikeSupported: spikes.supported,
        switchCount: switches.count,
        switchWorstMs: switches.worstMs,
        switchAvgMs: switches.avgTotalMs
      };
      setStats(next);
      // Feed the latest numbers to the baseline collector so captureBaseline()
      // can snapshot the current run on demand (Stats is a structural superset
      // of HudSnapshot).
      updateHudSnapshot(next);

      // Keep the recorder's live frame count fresh while capturing.
      if (isRecording()) setRecFrames(getFrameCount());
      // Reflect replay state so the pause/stop buttons + progress show (setState
      // bails out when the value is unchanged, so this is cheap).
      setReplayActive(isReplaying());
      setReplayPausedUi(isReplayPaused());
      if (isReplaying()) {
        const prog = getReplayProgress();
        setReplayIndexUi(prog.index);
        setReplayTotalUi(prog.total);
      }

      // Drive an active benchmark: feed this sample, update the countdown, and
      // finalize when the timer elapses. The HUD tick owns the timing so the
      // benchmark module needs no interval of its own.
      if (isBenchmarkRunning()) {
        sampleBenchmark(next);
        // A manual-end run (replay benchmark) is finished by the replay, not the
        // clock — just keep sampling; onRecReplay finalizes it when replay ends.
        if (!isBenchmarkManualEnd()) {
          const remaining = benchmarkRemainingMs();
          if (remaining <= 0) {
            const report = finishBenchmark(); // persists + logs the report
            setBenchRemainingSec(null);
            setBenchPhase('');
            cancelObserve(); // stop the open sequence if it's somehow still going
            setHasReport(true);
            if (report) setReportModal(report); // show it centered on screen
            // Return to the lobby so the benchmark control (and ⇪ share) is
            // visible again — the run navigated us onto the observed table.
            navigate('/');
          } else {
            setBenchRemainingSec(Math.ceil(remaining / 1000));
          }
        }
      }

      // Reset the frame window for the next sample period.
      frameTimesRef.current = [];
    }, SAMPLE_MS);

    return () => {
      dragCleanupRef.current?.();
      cancelAnimationFrame(rafIdRef.current);
      window.clearInterval(sampleId);
      setLatencyProbe(false);
      stopSpikeRecorder();
      setSwitchProfilingEnabled(false);
      delete (window as any).dumpSwitches;
      delete (window as any).dumpSpikes;
      delete (window as any).captureBaseline;
      delete (window as any).dumpBaseline;
      delete (window as any).getBaseline;
      delete (window as any).clearBaseline;
      delete (window as any).runBenchmark;
      delete (window as any).observeBenchmark;
      delete (window as any).handRec;
      delete (window as any).dumpBenchmarks;
      delete (window as any).getBenchmarks;
      delete (window as any).shareBenchmark;
      delete (window as any).clearBenchmarks;
      // Stop any in-progress observe sequence so it can't keep navigating after
      // the HUD closes.
      cancelObserve();
      // Reset the panel to its default top-right position on close, so reopening
      // always starts fresh.
      setPos(null);
      try {
        window.localStorage.removeItem(POS_STORAGE_KEY);
      } catch {
        // localStorage unavailable — the in-memory reset above still applies.
      }
    };
  }, [visible]);

  if (!visible) return null;

  // Drag the panel to reposition it (it can otherwise cover table controls).
  // Starts only from the panel body — pointerdowns on any action button are
  // ignored. The final position is clamped on-screen and persisted.
  const onPointerDownDrag = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, [role="button"]')) return;
    dragCleanupRef.current?.();
    const rect = panelRef.current?.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = pos?.left ?? rect?.left ?? 0;
    const startTop = pos?.top ?? rect?.top ?? 0;
    didDragRef.current = false;
    let last: HudPos = { left: startLeft, top: startTop };

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!didDragRef.current && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      didDragRef.current = true;
      const w = panelRef.current?.offsetWidth ?? 224;
      const h = panelRef.current?.offsetHeight ?? 120;
      // Keep a sliver on-screen so it can always be grabbed again.
      const left = Math.min(Math.max(0, startLeft + dx), window.innerWidth - Math.min(w, 60));
      const top = Math.min(Math.max(0, startTop + dy), window.innerHeight - Math.min(h, 40));
      last = { left, top };
      setPos(last);
    };
    const removeDragListeners = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      dragCleanupRef.current = null;
    };
    const onUp = () => {
      removeDragListeners();
      if (didDragRef.current) savePos(last);
    };
    const onCancel = () => removeDragListeners();
    dragCleanupRef.current = onCancel;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  // Dump a sorted breakdown of where every active tween lives by DOM ancestor
  // path. The dedicated button keeps this diagnostic available to keyboard and
  // assistive-technology users without making the draggable panel one giant control.
  const dumpTweens = () => {
    const tweens = gsap.globalTimeline.getChildren(true, true, false);
    const grouped: Record<string, number> = {};
    tweens.forEach((tween) => {
      const target = tween.targets()[0];
      if (!target || !(target instanceof Element)) return;
      let n: Element | null = target;
      const path: string[] = [];
      while (n && n !== document.body && path.length < 4) {
        const cls =
          (typeof (n as any).className === 'string' ? (n as any).className.split(' ')[0] : '') ||
          n.tagName;
        path.unshift(cls);
        n = n.parentElement;
      }
      const key = path.join(' > ').slice(0, 80);
      grouped[key] = (grouped[key] || 0) + 1;
    });
    const rows = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .map(([path, count]) => ({ path, count }));
    // eslint-disable-next-line no-console
    console.table(rows);
    // eslint-disable-next-line no-console
    console.log(`Total tweens: ${tweens.length}`);

    // Also dump the frame-spike attribution: which code ran during slow frames.
    // This is the "hotspots causing the spikes" answer — the whole reason the
    // recorder exists. Shared with the console `dumpSpikes()` hook.
    logSpikeSummary();

    // …and the per-switch breakdown, so one tap answers both "what is slow" and
    // "how much of it is table switching".
    logSwitchProfiles();
  };

  // Close (✕) the HUD and persist the same per-player preference controlled by
  // the Settings toggle, so every entry point remains consistent.
  const closeHud = () => {
    if (playerGUID) setPlayerPerfHudEnabled(playerGUID, false);
    setEnabled(false);
  };

  // Reset (↺) the session peaks/lows. Lets a tester re-baseline after the
  // startup/navigation spikes settle (or before exercising a new screen) so the
  // extremes reflect what they're actually testing. Does NOT re-arm the warm-up
  // — a manual reset means "track from right now."
  const resetPeaks = () => {
    fpsMinRef.current = 0;
    worstFramePeakRef.current = 0;
    wsRttPeakRef.current = -1;
    heapPeakMbRef.current = -1;
    tweensPeakRef.current = 0;
    animsPeakRef.current = -1;
    resetSpikes();
    resetSwitchProfiles();
    prevSpikeTotalRef.current = 0;
    peakWarmupUntilRef.current = performance.now(); // end warm-up now: track immediately
    setStats((s) => ({
      ...s,
      fpsMin: 0,
      worstFrameMaxMs: 0,
      wsRttMaxMs: -1,
      heapPeakMb: s.heapUsedMb >= 0 ? s.heapUsedMb : -1,
      activeTweensMax: s.activeTweens,
      runningAnimsMax: s.runningAnims,
      spikeCount: 0,
      spikeWorstMs: 0,
      spikesInWindow: 0,
      switchCount: 0,
      switchWorstMs: 0,
      switchAvgMs: 0
    }));
  };

  // Start a timed benchmark from the HUD (▶). Prompts for a label so QA can mark
  // before/after runs; falls back to a default if prompt is unavailable. The
  // sample tick drives the countdown and finalizes the report when time's up.
  const BENCH_DURATION_SEC = 60;

  // Prompt for a run label so QA can mark before/after. Returns null if the
  // tester cancels; falls back to a default if prompt is unavailable.
  const promptLabel = (fallback: string): string | null => {
    try {
      const input = window.prompt('Benchmark label (e.g. "before feature X"):', fallback);
      if (input === null) return null; // cancelled
      return input.trim() || fallback;
    } catch {
      return fallback; // prompt unavailable — use the default label
    }
  };

  // Benchmark the full "open + observe up to OBSERVE_TABLE_COUNT tables" flow.
  // Measurement starts FIRST, then the tables are opened one after another as an
  // observer (no seat taken — the same action as clicking a table in the lobby,
  // via BenchmarkTableBridge), routing through the lobby between each. Opening
  // and navigating is normal player use, so its cost is captured in the run;
  // whatever time is left after the last table opens measures the steady multi-
  // table load.
  const startObserveBench = async () => {
    if (!isTableControllerReady()) {
      // Bridge only exists past login/lobby — nudge the tester there.
      try {
        window.alert('Open the app to the lobby first, then run the benchmark.');
      } catch {
        // eslint-disable-next-line no-console
        console.warn('observeBenchmark: table controller not ready (not in the app yet).');
      }
      return;
    }
    // Confirm there's something to observe before we start measuring.
    if (pickLiveTables(OBSERVE_TABLE_COUNT).length === 0) {
      try {
        window.alert('No live tables with players found. Let the lobby load and try again.');
      } catch {
        // eslint-disable-next-line no-console
        console.warn('observeBenchmark: no live tables with seated players in the lobby.');
      }
      return;
    }
    const label = promptLabel(`${OBSERVE_TABLE_COUNT} tables`);
    if (label === null) return;

    // Start measuring immediately — the open/observe navigation is part of the run.
    startBenchmark(label, BENCH_DURATION_SEC);
    setBenchRemainingSec(BENCH_DURATION_SEC);
    setBenchPhase(`opening 0/${OBSERVE_TABLE_COUNT}`);
    // Open the tables during the measured window; the sample tick handles the
    // countdown + finish, so we don't need to await this for timing.
    void observeTables(OBSERVE_TABLE_COUNT, {
      onProgress: (i, total) => setBenchPhase(`opening ${i}/${total}`)
    }).then(() => setBenchPhase(''));
  };
  observeHandlerRef.current = startObserveBench;

  // Share the most recent report (⇪). Runs from this tap (a user gesture) so the
  // native share sheet isn't blocked; falls back to clipboard then console.
  const shareLastBench = () => {
    void shareBenchmark();
  };

  // Hand-recorder buttons. Start captures table frames; stop prints the size
  // report; export downloads the JSON; replay re-runs it through the real path.
  const onRecStart = () => {
    startRecording();
    setRecActive(true);
    setRecFrames(0);
    setRecHasCapture(false);
  };
  const onRecStop = () => {
    stopRecording(); // logs the size report to the console
    setRecActive(false);
    setRecFrames(getFrameCount());
    setRecHasCapture(getFrameCount() > 0);
  };
  // Import a previously-exported recording .json so it can be replayed. In-memory
  // captures don't survive a reload/HMR, so this is how you bring one back.
  const onRecImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-importing the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rec = JSON.parse(String(reader.result));
        if (loadRecording(rec)) {
          setRecHasCapture(true);
          setRecFrames(getFrameCount());
        } else {
          window.alert('That file is not a valid recording.');
        }
      } catch {
        window.alert('Could not read that recording file.');
      }
    };
    reader.readAsText(file);
  };

  // Replay in an isolated view so it doesn't hijack/overlap a live table. Gate
  // live frames first, then open the right view: one captured table → that
  // table's single view; several → the multi-table grid (max-load case). Let it
  // mount, replay, then clean up and return to the lobby.
  // withBench=true also measures the replay and emits a benchmark report that
  // ends exactly when the replay ends (manual-end run, not a fixed timer).
  const onRecReplay = (withBench = false) => {
    const tables = getCaptureTables();
    if (tables.length === 0) {
      try {
        window.alert('This recording has no table data to replay.');
      } catch {
        /* noop */
      }
      return;
    }
    let label = 'replay';
    if (withBench) {
      const chosen = promptLabel(tables.length > 1 ? `${tables.length}-table replay` : 'replay');
      if (chosen === null) return; // cancelled
      label = chosen;
    }
    setReplaying(true); // pause live frames before opening the view
    const multi = tables.length > 1;
    if (multi) {
      // Seed only the first table so the grid opens with one cell; the replay
      // then ramps the rest in one at a time (staggered). Use the controller's
      // openGrid (real view transition) — a raw navigate('/grid') doesn't switch
      // the view.
      primeFirstReplayTable();
      if (isTableControllerReady()) openGridViaController();
      else navigate('/grid');
    } else if (isTableControllerReady()) {
      openObserveTable(tables[0].gameID, tables[0].gameType);
    } else {
      navigate(`/table/${tables[0].gameID}/${tables[0].gameType}?observe=yes`);
    }
    // Reproduce recorded view/mode switches by driving the real transitions, so
    // the cost of switching table views is part of the measured replay.
    const onReplayNav = (path: string) => {
      if (!isTableControllerReady()) return;
      const tableMatch = path.match(/^\/table\/([^/?]+)\/([^/?]+)/);
      if (path.startsWith('/grid')) openGridViaController();
      else if (tableMatch) openObserveTable(tableMatch[1], tableMatch[2]);
      else returnToLobbyViaController();
    };
    window.setTimeout(() => {
      // Start measuring right as replay begins (manual end → the replay finishing
      // finalizes the report). resetSpikes inside startBenchmark scopes offenders
      // to this replay.
      if (withBench) startBenchmark(label, 0, { manualEnd: true, replaySpeed: replaySpeedUi });
      void replayRecording(undefined, replaySpeedUi, onReplayNav).finally(() => {
        if (withBench) {
          const report = finishBenchmark(); // persists + logs the report now that replay ended
          setHasReport(true);
          if (report) setReportModal(report); // show it centered on screen
        }
        setReplaying(false);
        gsap.globalTimeline.resume(); // clear any pause left over
        if (multi) clearReplayedTables(); // don't leave grid tables lingering
        if (isTableControllerReady()) returnToLobbyViaController();
        else navigate('/');
      });
    }, 600);
  };

  // Cycle replay speed 1× → 2× → 4× → 8× → 1×. Applies immediately (even mid-
  // replay, since the loop reads the speed each frame).
  const REPLAY_SPEEDS = [1, 2, 4, 8];
  const cycleReplaySpeed = () => {
    const next = REPLAY_SPEEDS[(REPLAY_SPEEDS.indexOf(replaySpeedUi) + 1) % REPLAY_SPEEDS.length];
    setReplaySpeedUi(next);
    setReplaySpeed(next);
  };

  // Replay pause/resume/stop. Pausing the frame stream alone isn't enough — the
  // seat timer / animations are GSAP-driven off wall-clock, so we pause the GSAP
  // global timeline too (that's why the countdown kept ticking before).
  const onReplayPause = () => {
    pauseReplay();
    gsap.globalTimeline.pause();
  };
  const onReplayResume = () => {
    resumeReplay();
    gsap.globalTimeline.resume();
  };
  const onReplayStop = () => {
    stopReplay();
    gsap.globalTimeline.resume();
  };

  // Color the FPS readout for at-a-glance scanning. Reused for the session low.
  const fpsColorFor = (f: number) => (f >= 55 ? '#4ade80' : f >= 40 ? '#fbbf24' : '#f87171');
  const fpsColor = fpsColorFor(stats.fps);
  const fpsMinColor = stats.fpsMin > 0 ? fpsColorFor(stats.fpsMin) : '#9ca3af';
  // Frame-time thresholds, reused for both the rolling value and the session peak.
  const frameColor = (ms: number) => (ms <= 20 ? '#4ade80' : ms <= 40 ? '#fbbf24' : '#f87171');
  const worstColor = frameColor(stats.worstFrameMs);
  const worstPeakColor = frameColor(stats.worstFrameMaxMs);
  // Spikes: green while none are landing, amber on a single fresh spike this
  // window, red on a burst (≥2 long frames in one 250ms sample). The worst-ms
  // is colored by the frame-time scale — a 100ms+ spike is red regardless.
  const spikeColor =
    stats.spikesInWindow === 0 ? '#4ade80' : stats.spikesInWindow < 2 ? '#fbbf24' : '#f87171';
  // A rising post-GC floor is the real leak signal. Green flat, amber creeping,
  // red climbing. Neutral grey until we have enough buckets to trust it.
  const leakColor = !stats.heapLeakReady
    ? '#9ca3af'
    : stats.heapLeakMbPerMin < 0.5
    ? '#4ade80'
    : stats.heapLeakMbPerMin < 2
    ? '#fbbf24'
    : '#f87171';
  const memSupported = stats.heapUsedMb >= 0;
  // Decompression CPU is the idle-heat tell: 1000ms/s = a full core pegged.
  // Green under ~50ms/s, amber to ~150, red beyond — sustained main-thread work.
  const decompColor =
    stats.decompressMsPerSec < 50
      ? '#4ade80'
      : stats.decompressMsPerSec < 150
      ? '#fbbf24'
      : '#f87171';
  // API Gateway RTT: green under 100ms, amber to 250, red beyond. Grey when we
  // have no fresh sample (probe just started, or pongs aren't coming back).
  // RTT thresholds. The live value greys out when stale; the peak is colored by
  // its own magnitude (a recorded worst RTT is meaningful even once it's stale).
  const rttColorFor = (ms: number) =>
    ms < 0 ? '#9ca3af' : ms < 100 ? '#4ade80' : ms < 250 ? '#fbbf24' : '#f87171';
  const rttColor = stats.wsRttMs < 0 || stats.wsRttStale ? '#9ca3af' : rttColorFor(stats.wsRttMs);
  const rttPeakColor = rttColorFor(stats.wsRttMaxMs);
  const rttText = stats.wsRttMs < 0 ? '…' : `${stats.wsRttMs}ms`;
  const rttPeakText = stats.wsRttMaxMs < 0 ? '—' : `${stats.wsRttMaxMs}ms`;
  const actionAgeText = stats.actionAgeMs < 0 ? '—' : `${(stats.actionAgeMs / 1000).toFixed(1)}s`;

  return (
    <>
      {reportModal ? (
        <BenchmarkReportModal report={reportModal} onClose={() => setReportModal(null)} />
      ) : null}
      <div
        data-testid='perf-hud'
        role='group'
        aria-label='Performance HUD diagnostics'
        ref={panelRef}
        onPointerDown={onPointerDownDrag}
        style={{
          position: 'fixed',
          // Once dragged, pin to the saved left/top; otherwise anchor top-right,
          // clearing the iPhone notch / Dynamic Island / status bar.
          ...(pos
            ? { left: pos.left, top: pos.top }
            : {
                top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
                right: 'calc(env(safe-area-inset-right, 0px) + 8px)'
              }),
          zIndex: 999999,
          backgroundColor: 'rgba(0,0,0,0.78)',
          color: '#fff',
          fontFamily: 'monospace',
          fontSize: 10,
          lineHeight: 1.35,
          padding: '6px 8px',
          borderRadius: 6,
          pointerEvents: 'auto',
          // Draggable to move it off table controls; still tap-to-dump.
          cursor: 'move',
          // Don't let a drag on the panel scroll the page underneath.
          touchAction: 'none',
          // Fixed width + border-box + nowrap so the panel never resizes as the
          // numbers change digit count each sample (that caused the jitter).
          // tabular-nums keeps digits monospaced-aligned so values don't shift.
          boxSizing: 'border-box',
          width: 224,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          border: '1px solid rgba(255,255,255,0.12)',
          userSelect: 'none'
        }}>
        {/* Explicit controls keep diagnostic actions separate from dragging. */}
        <button
          type='button'
          aria-label='Dump animation diagnostics'
          title='Dump animation diagnostics'
          onClick={(e) => {
            e.stopPropagation();
            dumpTweens();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            dumpTweens();
          }}
          style={{
            ...HUD_BUTTON_STYLE,
            position: 'absolute',
            top: 0,
            right: 52,
            padding: '4px 6px',
            fontSize: 13,
            lineHeight: 1,
            color: '#60a5fa',
            cursor: 'pointer'
          }}>
          ≡
        </button>
        <button
          type='button'
          aria-label='Reset perf HUD peaks'
          title='Reset session peaks/lows'
          onClick={(e) => {
            e.stopPropagation();
            resetPeaks();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            resetPeaks();
          }}
          style={{
            ...HUD_BUTTON_STYLE,
            position: 'absolute',
            top: 0,
            right: 26,
            padding: '4px 6px',
            fontSize: 13,
            lineHeight: 1,
            color: '#9ca3af',
            cursor: 'pointer'
          }}>
          ↺
        </button>
        <button
          type='button'
          aria-label='Close perf HUD'
          title='Close perf HUD'
          onClick={(e) => {
            e.stopPropagation();
            closeHud();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            closeHud();
          }}
          style={{
            ...HUD_BUTTON_STYLE,
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '4px 8px',
            fontSize: 13,
            lineHeight: 1,
            color: '#f87171',
            cursor: 'pointer'
          }}>
          ✕
        </button>
        <div style={{ paddingRight: 70 }}>
          FPS <span style={{ color: fpsColor }}>{stats.fps}</span>
          <span style={{ opacity: 0.6 }}> low </span>
          <span style={{ color: fpsMinColor }}>{stats.fpsMin > 0 ? stats.fpsMin : '…'}</span>
        </div>
        <div>
          worst frame <span style={{ color: worstColor }}>{stats.worstFrameMs.toFixed(0)}ms</span>
          <span style={{ opacity: 0.6 }}> peak </span>
          <span style={{ color: worstPeakColor }}>{stats.worstFrameMaxMs.toFixed(0)}</span>
        </div>
        <div
          title={
            'Long-frame spikes (Long Animation Frames API, or longtask fallback). ' +
            'A "spike" is any frame that took ≥50ms — the jank you feel. The number ' +
            'is the session total; +N flags spikes in the last 250ms (spiking now); ' +
            'worst is the worst blocking time seen. Use the dump button for a table of ' +
            'which functions/scripts ran during the slow frames — the code hotspots ' +
            'causing the spikes. n/a on iOS (no LoAF/longtask — use Safari Timelines).'
          }>
          {stats.spikeSupported ? (
            <>
              spikes <span style={{ color: spikeColor }}>{stats.spikeCount}</span>
              {stats.spikesInWindow > 0 ? (
                <span style={{ color: spikeColor }}> +{stats.spikesInWindow}</span>
              ) : null}
              <span style={{ opacity: 0.6 }}> worst {stats.spikeWorstMs.toFixed(0)}ms</span>
              {/* The attribution is the whole point of the recorder and the
                  console is unreachable in the installed app — see
                  shareSpikeSummary. */}
              {stats.spikeCount > 0 ? (
                <button
                  type='button'
                  aria-label='Share spike report'
                  onClick={(e) => {
                    e.stopPropagation();
                    void shareSpikeSummary();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void shareSpikeSummary();
                  }}
                  style={{ ...HUD_BUTTON_STYLE, color: '#60a5fa', cursor: 'pointer' }}>
                  {' '}
                  ⇪
                </button>
              ) : null}
            </>
          ) : (
            <span style={{ opacity: 0.6 }}>spikes n/a (iOS)</span>
          )}
        </div>
        <div
          title={
            'Table-switch cost. n = switches measured this session; worst = the worst ' +
            'blocking frame that landed within 700ms of a switch; avg = mean main-thread ' +
            'ms blocked per switch. This separates "switching tables is janky" from ' +
            '"the app is janky". Use the dump button (or call dumpSwitches()) for a per-switch ' +
            'table with the top offending script. Needs LoAF, so n/a on iOS.'
          }>
          switch <span style={{ color: '#9ca3af' }}>{stats.switchCount}</span>
          {/* Cost is attributed from the spike stream (switchProfile asks the
              recorder which long frames landed in the window), so with no LoAF
              there is no input and every switch costs a measured-looking 0ms.
              Reporting that as "0ms" read as "switching is free" on an iOS
              capture where nothing had been measured at all — say so instead. */}
          {!stats.spikeSupported ? (
            <span style={{ opacity: 0.6 }}> cost n/a (iOS)</span>
          ) : stats.switchCount > 0 ? (
            <>
              <span style={{ opacity: 0.6 }}> worst </span>
              <span style={{ color: frameColor(stats.switchWorstMs) }}>
                {stats.switchWorstMs}ms
              </span>
              <span style={{ opacity: 0.6 }}> avg {stats.switchAvgMs}ms</span>
            </>
          ) : (
            <span style={{ opacity: 0.6 }}> — switch tables to measure</span>
          )}
        </div>
        <div
          title={
            'rtt = API Gateway WebSocket round-trip: client ping -> server -> pong. ' +
            'NOT pure network latency — the server ping route also does an RDS write + ' +
            'DynamoDB TTL refresh before replying, so this includes server processing time. ' +
            'Use it as a "is the connection healthy/responsive" signal, not a raw network ping. ' +
            'peak = the worst RTT seen this session (survives the live reading going stale). ' +
            'last msg = seconds since the last Socket.IO (action socket) message; stream ' +
            'liveness, not latency (the engine has no benign echo to time a round-trip against).'
          }>
          rtt <span style={{ color: rttColor }}>{rttText}</span>
          <span style={{ opacity: 0.6 }}> peak </span>
          <span style={{ color: rttPeakColor }}>{rttPeakText}</span>
          <span style={{ opacity: 0.6 }}> last msg {actionAgeText}</span>
        </div>
        <div>
          msg/s {stats.msgsPerSec}
          <span style={{ color: decompColor }}> decomp {stats.decompressMsPerSec}ms/s</span>
        </div>
        {memSupported ? (
          <>
            <div>
              mem {stats.heapUsedMb.toFixed(0)}MB
              <span style={{ opacity: 0.6 }}> peak {stats.heapPeakMb.toFixed(0)}</span>
            </div>
            <div
              title={
                'Memory-leak signal. The live JS heap sawtooths (climbs, then drops on every ' +
                'garbage collection), so a raw growth rate is useless noise. Instead we track ' +
                'the post-GC FLOOR: the lowest heap seen after a collection — your true resident ' +
                'baseline, the memory that could NOT be freed. "floor NNN" is that baseline in MB. ' +
                'The +N.N MB/min number is the slope of the floor over the last few minutes: a ' +
                'floor that ratchets upward = a real leak. Green flat, amber creeping, red climbing; ' +
                'grey "…" until enough history (~30s) to trust the trend.'
              }>
              leak{' '}
              <span style={{ color: leakColor }}>
                {stats.heapLeakReady
                  ? `${stats.heapLeakMbPerMin >= 0 ? '+' : ''}${stats.heapLeakMbPerMin.toFixed(
                      1
                    )}MB/min`
                  : '…'}
              </span>
              <span style={{ opacity: 0.6 }}> floor {stats.heapFloorMb.toFixed(0)}</span>
            </div>
          </>
        ) : (
          <div style={{ opacity: 0.6 }}>mem n/a (iOS)</div>
        )}
        <div>
          tweens {stats.activeTweens}
          <span style={{ opacity: 0.6 }}> peak {stats.activeTweensMax}</span>
          {stats.runningAnims >= 0 ? (
            <span style={{ opacity: 0.6 }}>
              {' '}
              anims {stats.runningAnims} peak {stats.runningAnimsMax}
            </span>
          ) : (
            ''
          )}
        </div>
        <HandPaceRows />
        {/* Benchmark control row — only in the lobby (where a table can be opened),
          or while a run is in progress. Buttons stopPropagation so tapping them
          doesn't also fire the panel's tween-dump. */}
        {isLobby || benchRemainingSec !== null ? (
          <div
            style={{
              marginTop: 4,
              paddingTop: 4,
              borderTop: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            title={
              `QA benchmark: opens the ${OBSERVE_TABLE_COUNT} busiest live tables as an ` +
              `observer one after another, measures ${BENCH_DURATION_SEC}s with all of them ` +
              'streaming, returns to the lobby, then tap ⇪ to share. Compare runs with ' +
              'dumpBenchmarks().'
            }>
            {benchRemainingSec !== null ? (
              <span style={{ color: '#fbbf24' }}>
                ● BENCH {benchRemainingSec}s{benchPhase ? ` · ${benchPhase}` : ''}
              </span>
            ) : (
              <>
                <button
                  type='button'
                  aria-label='Observe live tables and benchmark them'
                  onClick={(e) => {
                    e.stopPropagation();
                    void startObserveBench();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void startObserveBench();
                  }}
                  style={{ ...HUD_BUTTON_STYLE, color: '#4ade80', cursor: 'pointer' }}>
                  ▶ benchmark {OBSERVE_TABLE_COUNT} tables
                </button>
                {hasReport ? (
                  <button
                    type='button'
                    aria-label='Share last benchmark report'
                    onClick={(e) => {
                      e.stopPropagation();
                      shareLastBench();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      shareLastBench();
                    }}
                    style={{ ...HUD_BUTTON_STYLE, color: '#60a5fa', cursor: 'pointer' }}>
                    ⇪ share
                  </button>
                ) : null}
              </>
            )}
          </div>
        ) : null}
        {/* Hand recorder row (prototype). Record table frames, then export/replay.
          Buttons stopPropagation so tapping them doesn't dump tweens or drag. */}
        <div
          style={{
            marginTop: 4,
            paddingTop: 4,
            borderTop: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 6,
            rowGap: 4
          }}
          title={
            'Hand recorder (prototype): sit on/observe a table, ⏺ record, play a hand, ' +
            '⏹ stop (size report prints to console), then ⇩ export the JSON or ▶ replay it. ' +
            '⇧ import loads an exported .json to replay.'
          }>
          <input
            ref={importInputRef}
            type='file'
            accept='application/json,.json'
            style={{ display: 'none' }}
            onChange={onRecImportFile}
          />
          {replayActive ? (
            <>
              <span style={{ color: '#fbbf24' }}>
                ▶ {replayIndexUi}/{replayTotalUi}
                {replayTotalUi > 0
                  ? ` (${Math.round((replayIndexUi / replayTotalUi) * 100)}%)`
                  : ''}
                {replayPausedUi ? ' paused' : ''}
              </span>
              <button
                type='button'
                aria-label='Cycle replay speed'
                onClick={(e) => {
                  e.stopPropagation();
                  cycleReplaySpeed();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  cycleReplaySpeed();
                }}
                style={{ ...HUD_BUTTON_STYLE, color: '#4ade80', cursor: 'pointer' }}>
                {replaySpeedUi}×
              </button>
              <button
                type='button'
                aria-label={replayPausedUi ? 'Resume replay' : 'Pause replay'}
                onClick={(e) => {
                  e.stopPropagation();
                  replayPausedUi ? onReplayResume() : onReplayPause();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  replayPausedUi ? onReplayResume() : onReplayPause();
                }}
                style={{ ...HUD_BUTTON_STYLE, color: '#60a5fa', cursor: 'pointer' }}>
                {replayPausedUi ? '▶ resume' : '⏸ pause'}
              </button>
              <button
                type='button'
                aria-label='Stop replay'
                onClick={(e) => {
                  e.stopPropagation();
                  onReplayStop();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onReplayStop();
                }}
                style={{ ...HUD_BUTTON_STYLE, color: '#f87171', cursor: 'pointer' }}>
                ⏹ stop
              </button>
            </>
          ) : (
            <>
              {recActive ? (
                <button
                  type='button'
                  aria-label='Stop recording'
                  onClick={(e) => {
                    e.stopPropagation();
                    onRecStop();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRecStop();
                  }}
                  style={{ ...HUD_BUTTON_STYLE, color: '#f87171', cursor: 'pointer' }}>
                  ⏹ stop rec ({recFrames}f)
                </button>
              ) : (
                <>
                  <button
                    type='button'
                    aria-label='Start recording table frames'
                    onClick={(e) => {
                      e.stopPropagation();
                      onRecStart();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRecStart();
                    }}
                    style={{ ...HUD_BUTTON_STYLE, color: '#f87171', cursor: 'pointer' }}>
                    ⏺ record
                  </button>
                  <button
                    type='button'
                    aria-label='Import a recording file'
                    onClick={(e) => {
                      e.stopPropagation();
                      importInputRef.current?.click();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      importInputRef.current?.click();
                    }}
                    style={{ ...HUD_BUTTON_STYLE, color: '#60a5fa', cursor: 'pointer' }}>
                    ⇧ import
                  </button>
                </>
              )}
              {!recActive && recHasCapture ? (
                <>
                  <span style={{ opacity: 0.6 }}>{recFrames}f</span>
                  <button
                    type='button'
                    aria-label='Export recording as JSON'
                    onClick={(e) => {
                      e.stopPropagation();
                      exportRecording();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      exportRecording();
                    }}
                    style={{ ...HUD_BUTTON_STYLE, color: '#60a5fa', cursor: 'pointer' }}>
                    ⇩ export
                  </button>
                  <button
                    type='button'
                    aria-label='Replay recording'
                    onClick={(e) => {
                      e.stopPropagation();
                      onRecReplay();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRecReplay();
                    }}
                    style={{ ...HUD_BUTTON_STYLE, color: '#4ade80', cursor: 'pointer' }}>
                    ▶ replay
                  </button>
                  <button
                    type='button'
                    aria-label='Replay and produce a benchmark report'
                    title='Replay + benchmark report (ends when the replay ends)'
                    onClick={(e) => {
                      e.stopPropagation();
                      onRecReplay(true);
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRecReplay(true);
                    }}
                    style={{ ...HUD_BUTTON_STYLE, color: '#4ade80', cursor: 'pointer' }}>
                    📊 bench
                  </button>
                  <button
                    type='button'
                    aria-label='Cycle replay speed'
                    title='Replay speed'
                    onClick={(e) => {
                      e.stopPropagation();
                      cycleReplaySpeed();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      cycleReplaySpeed();
                    }}
                    style={{
                      ...HUD_BUTTON_STYLE,
                      color: '#4ade80',
                      cursor: 'pointer',
                      opacity: 0.85
                    }}>
                    {replaySpeedUi}×
                  </button>
                  <button
                    type='button'
                    aria-label='Free the in-memory capture'
                    title='Free the capture (reclaim its memory)'
                    onClick={(e) => {
                      e.stopPropagation();
                      clearCapture();
                      setRecFrames(0);
                      setRecHasCapture(false);
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      clearCapture();
                      setRecFrames(0);
                      setRecHasCapture(false);
                    }}
                    style={{
                      ...HUD_BUTTON_STYLE,
                      color: '#f87171',
                      cursor: 'pointer',
                      opacity: 0.85
                    }}>
                    🗑 clear
                  </button>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PerfHud;
